<template>
  <div
    v-show="visible"
    ref="floatingWindow"
    class="tw:fixed tw:shadow-2xl tw:overflow-hidden tw:select-none tw:backdrop-blur-sm tw:max-w-full tw:max-h-full tw:rounded-lg"
    :class="windowZClass"
    :style="windowStyle"
    @mousedown="startDrag"
  >
    <!-- Minimized state: show only the minimal bar -->
    <ToolboxBarJitsi
      v-if="minimized"
      :minimized="minimized"
      :minimize="minimize"
      :close="close"
    />

    <TopBarJitsi
      :minimized="minimized"
      :minimize="minimize"
      :minimizable="minimizable"
      :isFullscreen="isFullscreen"
      :fullscreenable="fullscreenable"
      :toggleFullscreen="toggleFullscreen"
      :closable="closable"
      :close="close"
      :startDrag="startDrag"
    />

    <!-- Content area: always present, size matches minimized state when minimized -->
    <div
      :style="contentStyle as CSSProperties"
      :class="['tw:overflow-hidden tw:p-0 tw:w-full', { 'tw:z-layer-1': minimized }]"
    >
      <div
        class="tw:p-4 tw:w-full tw:overflow-auto scrollbar-hide"
        :style="{ height: '100%' }"
      >
        <slot></slot>
      </div>
    </div>

    <!-- Resize handle -->
    <div
      v-if="resizable && !isFullscreen && !minimized"
      class="tw:absolute tw:bottom-0 tw:right-0 tw:w-4 tw:h-4 tw:cursor-se-resize tw:bg-transparent after:tw:content-[''] after:tw:absolute after:tw:bottom-1 after:tw:right-1 after:tw:w-0 after:tw:h-0 after:tw:border-l-8 after:tw:border-l-transparent after:tw:border-b-8 after:tw:border-b-gray-400"
      @mousedown.stop="startResize('se')"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { useJitsiStore } from "@/stores/jitsiStore";
import { ref, computed, onMounted, onUnmounted, type CSSProperties } from "vue";
import ToolboxBarJitsi from "./ToolboxBarJitsi.vue";
import TopBarJitsi from "./TopBarJitsi.vue";

// Props
const props = defineProps({
  title: {
    type: String,
    default: "Floating Window",
  },
  initialX: {
    type: Number,
    default: 100,
  },
  initialY: {
    type: Number,
    default: 100,
  },
  initialWidth: {
    type: Number,
    default: 400,
  },
  initialHeight: {
    type: Number,
    default: 300,
  },
  minWidth: {
    type: Number,
    default: 200,
  },
  minHeight: {
    type: Number,
    default: 150,
  },
  maxWidth: {
    type: Number,
    default: null,
  },
  maxHeight: {
    type: Number,
    default: null,
  },
  resizable: {
    type: Boolean,
    default: true,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  closable: {
    type: Boolean,
    default: true,
  },
  minimizable: {
    type: Boolean,
    default: true,
  },
  fullscreenable: {
    type: Boolean,
    default: true,
  },
  headerHeight: {
    type: Number,
    default: 40,
  },
});

// Emits
const emit = defineEmits(["close", "minimize", "resize", "move", "fullscreen"]);

// Methods
const { leaveRoom } = useJitsiStore();
// Reactive state
const visible = ref(true);
const minimized = ref(false);
const isFullscreen = ref(false);
// @ts-ignore
const floatingWindow = ref(null);

const position = ref({
  x: props.initialX,
  y: props.initialY,
});

const size = ref({
  width: props.initialWidth,
  height: props.initialHeight,
});

// Store original state before fullscreen
const beforeFullscreen = ref({
  x: props.initialX,
  y: props.initialY,
  width: props.initialWidth,
  height: props.initialHeight,
});

const dragging = ref(false);
const resizing = ref(false);
const resizeDirection = ref("");
const isTransitioning = ref(false);
const transitionTimeoutId = ref<NodeJS.Timeout | null>(null); // Add timeout ID tracking

const dragStart = ref({ x: 0, y: 0 });
const resizeStart = ref({ x: 0, y: 0, width: 0, height: 0 });

const windowZClass = computed(() =>
  isFullscreen.value ? "tw:z-modal-overlay-elevated" : "tw:z-modal-overlay"
);

const windowStyle = computed(() => {
  if (isFullscreen.value) {
    return {
      left: "0px",
      top: "0px",
      width: "100vw",
      height: "100dvh",
      transition: "all 0.3s ease",
    };
  }
  if (minimized.value) {
    return {
      left: `${position.value.x}px`,
      top: `${position.value.y}px`,
      width: "300px",
      height: "60px",
      transition: dragging.value ? "none" : "all 0.3s ease",
    };
  }
  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
    width: `${size.value.width}px`,
    height: `${size.value.height}px`,
    transition: dragging.value || resizing.value ? "none" : "all 0.3s ease",
  };
});

const contentStyle = computed(() => {
  if (minimized.value) {
    return {
      width: "300px",
      height: "60px",
      position: "absolute",
      top: "0px",
      left: "0px",
      overflow: "hidden",
    };
  }

  if (isFullscreen.value) {
    return {
      height: "100dvh",
      display: "block",
    };
  }

  return {
    height: "100%", // Full window height since bar is absolute
    display: "block",
  };
});

// Fullscreen functionality
const toggleFullscreen = () => {
  if (isFullscreen.value) {
    exitFullscreen();
  } else {
    enterFullscreen();
  }
};

const enterFullscreen = () => {
  // Store current state
  beforeFullscreen.value = {
    x: position.value.x,
    y: position.value.y,
    width: size.value.width,
    height: size.value.height,
  };

  isFullscreen.value = true;
  minimized.value = false; // Exit minimized state if in fullscreen

  emit("fullscreen", true);
};

const exitFullscreen = () => {
  // Clear any existing transition timeout
  if (transitionTimeoutId.value !== null) {
    clearTimeout(transitionTimeoutId.value);
    transitionTimeoutId.value = null;
  }

  // Enable transition for smooth exit
  isTransitioning.value = true;

  // Restore previous state
  position.value.x = beforeFullscreen.value.x;
  position.value.y = beforeFullscreen.value.y;
  size.value.width = beforeFullscreen.value.width;
  size.value.height = beforeFullscreen.value.height;

  isFullscreen.value = false;

  // Disable transition after animation completes
  transitionTimeoutId.value = setTimeout(() => {
    isTransitioning.value = false;
    transitionTimeoutId.value = null;
  }, 300);

  emit("fullscreen", false);
};

// Drag functionality
const startDrag = (e: MouseEvent) => {
  if (!props.draggable || resizing.value || isFullscreen.value) return;

  e.preventDefault();
  dragging.value = true;

  dragStart.value = {
    x: e.clientX - position.value.x,
    y: e.clientY - position.value.y,
  };

  document.addEventListener("mousemove", drag);
  document.addEventListener("mouseup", stopDrag);
};

const drag = (e: MouseEvent) => {
  if (!dragging.value || isFullscreen.value) return;

  const newX = e.clientX - dragStart.value.x;
  const newY = e.clientY - dragStart.value.y;

  // Use appropriate window dimensions based on minimized state
  const windowWidth = minimized.value ? 300 : size.value.width;
  const windowHeight = minimized.value ? 60 : size.value.height;

  const maxX = window.innerWidth - windowWidth;
  const maxY = window.innerHeight - windowHeight;

  position.value.x = Math.max(0, Math.min(newX, maxX));
  position.value.y = Math.max(0, Math.min(newY, maxY));

  emit("move", { x: position.value.x, y: position.value.y });
};

const stopDrag = () => {
  dragging.value = false;
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
};

// Resize functionality (simplified for only bottom-right corner)
const startResize = (direction: string) => {
  if (!props.resizable || isFullscreen.value || direction !== "se") return;

  resizing.value = true;
  resizeDirection.value = direction;

  resizeStart.value = {
    x: position.value.x,
    y: position.value.y,
    width: size.value.width,
    height: size.value.height,
  };

  document.addEventListener("mousemove", resize);
  document.addEventListener("mouseup", stopResize);
};

const resize = (e: MouseEvent) => {
  if (!resizing.value || isFullscreen.value || resizeDirection.value !== "se") return;

  const deltaX = e.clientX - (resizeStart.value.x + resizeStart.value.width);
  const deltaY = e.clientY - (resizeStart.value.y + resizeStart.value.height);

  // Only handle bottom-right corner resize (southeast)
  let newWidth = resizeStart.value.width + deltaX;
  let newHeight = resizeStart.value.height + deltaY;

  // Apply minimum constraints - use larger minimums when not minimized
  const minWidth = minimized.value ? 300 : 450;
  const minHeight = minimized.value ? 60 : 250;

  newWidth = Math.max(minWidth, newWidth);
  newHeight = Math.max(minHeight, newHeight);

  if (props.maxWidth) newWidth = Math.min(props.maxWidth, newWidth);
  if (props.maxHeight) newHeight = Math.min(props.maxHeight, newHeight);

  // Keep window within viewport bounds
  const maxWidth = window.innerWidth - position.value.x;
  const maxHeight = window.innerHeight - position.value.y;

  newWidth = Math.min(newWidth, maxWidth);
  newHeight = Math.min(newHeight, maxHeight);

  size.value.width = newWidth;
  size.value.height = newHeight;

  emit("resize", {
    width: newWidth,
    height: newHeight,
    x: position.value.x,
    y: position.value.y,
  });
};

const stopResize = () => {
  resizing.value = false;
  resizeDirection.value = "";
  document.removeEventListener("mousemove", resize);
  document.removeEventListener("mouseup", stopResize);
};

// Window controls
const close = () => {
  // Clear any pending timeouts before closing
  if (transitionTimeoutId.value !== null) {
    clearTimeout(transitionTimeoutId.value);
    transitionTimeoutId.value = null;
  }

  leaveRoom();
  visible.value = false;
  emit("close");
};

const minimize = () => {
  if (isFullscreen.value) {
    exitFullscreen();
  }

  const wasMinimized = minimized.value;
  minimized.value = !minimized.value;

  // Position to bottom-right when minimizing
  if (minimized.value) {
    const windowWidth = 300; // minimized width
    const windowHeight = 60; // minimized height
    const margin = 20; // margin from edges

    position.value.x = window.innerWidth - windowWidth - margin;
    position.value.y = window.innerHeight - windowHeight - margin;
  }
  // Position to center when un-minimizing
  else if (wasMinimized) {
    const centerX = (window.innerWidth - size.value.width) / 2;
    const centerY = (window.innerHeight - size.value.height) / 2;

    position.value.x = Math.max(0, centerX);
    position.value.y = Math.max(0, centerY);
  }

  emit("minimize", minimized.value);
};

// Keyboard shortcuts for fullscreen
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === "F11" && visible.value) {
    e.preventDefault();
    toggleFullscreen();
  }
  if (e.key === "Escape" && isFullscreen.value) {
    exitFullscreen();
  }
};

// Public methods
const show = () => {
  visible.value = true;
};

const hide = () => {
  visible.value = false;
};

const setPosition = (x: number, y: number) => {
  if (!isFullscreen.value) {
    position.value.x = x;
    position.value.y = y;
  }
};

const setSize = (width: any, height: any) => {
  if (!isFullscreen.value) {
    size.value.width = width;
    size.value.height = height;
  }
};

const setFullscreen = (fullscreen: boolean) => {
  if (fullscreen && !isFullscreen.value) {
    enterFullscreen();
  } else if (!fullscreen && isFullscreen.value) {
    exitFullscreen();
  }
};

// Expose methods
defineExpose({
  show,
  hide,
  setPosition,
  setSize,
  setFullscreen,
  toggleFullscreen,
  minimize: () => minimize(),
  close: () => close(),
  isFullscreen: () => isFullscreen.value,
  isMinimized: () => minimized.value,
});

// Setup and cleanup
onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

// Cleanup
onUnmounted(() => {
  // Clear any pending timeouts
  if (transitionTimeoutId.value !== null) {
    clearTimeout(transitionTimeoutId.value);
    transitionTimeoutId.value = null;
  }

  document.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("mousemove", resize);
  document.removeEventListener("mouseup", stopResize);
});
</script>
